import { useSnackbar } from 'notistack';
import { FormEvent, useMemo, useState } from 'react';
import {
  Control,
  FieldErrors,
  UseFormRegister,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import useSWR from 'swr';
import * as yup from 'yup';

import { fetchEmployee } from '~/data/employee';
import { useEmployee } from '~/data/employee/useEmployee';
import { Roles } from '~/data/user';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { employeeModulesAccessRoles } from '~/scenes/PortalViews/Views/EmployeeViews/data';
import { AppDispatch } from '~/store';
import { isValidPhoneNumber } from '~/utils/helper';

import {
  EmpOffContactType,
  Office,
  getCompanyOffices,
  putEmpOffContactData,
} from './data';

export type IFormInput = {
  fEmpEmailWork: string;
  fEmpOfficeFloor?: string;
  fEmpOfficeLoc: number;
  fEmpOfficeRoom?: string;
  fEmpPhoneMob?: string | undefined;
  fEmpPhoneWork?: string;
};

export type useWorkContractOutput = {
  readMode: boolean;
  setReadMode: (data: boolean) => void;
  empData: EmpOffContactType;
  onSubmit: (e: FormEvent) => void;
  register: UseFormRegister<IFormInput>;
  errors: FieldErrors<IFormInput>;
  control: Control<IFormInput>;
  companyOffice: Office[];
  handleFormReset: () => void;
  isLoading: boolean;
  canEdit: boolean;
};

export const useGetCompanyOffices = () => {
  const { data, ...rest } = useSWR(['companyOffices'], () =>
    getCompanyOffices(),
  );

  return {
    data: data ?? [],
    ...rest,
  };
};

export const useWorkContact = (): useWorkContractOutput => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();

  const {
    isSelectedEmployeeLoggedInUser,
    selectedEmployeeDetails: { hasRoleAccessOverEmployee, isMyselfWithRole },
  } = useSelectedEmployeeId();

  const { canEdit } = useMemo(() => {
    const rolesWithoutEditAccess = [Roles.DEPUTY_MANAGER];

    const canEditMyselfRoles = employeeModulesAccessRoles.people.full.filter(
      (r) => !rolesWithoutEditAccess.includes(r),
    );

    const canEditEmployeeRoles = employeeModulesAccessRoles.people.all.filter(
      (r) => !rolesWithoutEditAccess.includes(r),
    );

    return {
      canEdit: isSelectedEmployeeLoggedInUser
        ? isMyselfWithRole(canEditMyselfRoles)
        : hasRoleAccessOverEmployee(canEditEmployeeRoles),
    };
  }, [
    isSelectedEmployeeLoggedInUser,
    isMyselfWithRole,
    hasRoleAccessOverEmployee,
  ]);

  const {
    employee: { empWorkContact: empData, empBasicData },
    isLoading: isLoadingEmployee,
  } = useEmployee();

  const { data: companyOffice, isLoading: isLoadingCompanyOffices } =
    useGetCompanyOffices();

  const isLoading = isLoadingEmployee || isLoadingCompanyOffices;

  const [readMode, setReadMode] = useState(true);

  const validationSchema = useMemo(() => {
    return yup.object().shape<IFormInput>({
      fEmpEmailWork: yup.string().required(t('laFillManFields')),
      fEmpOfficeFloor: yup.string(),
      fEmpOfficeLoc: yup.number().required(t('laFillManFields')),
      fEmpOfficeRoom: yup.string(),
      fEmpPhoneMob: yup
        .string()
        .test('phoneNumberValidation', t('phoneInvalid'), isValidPhoneNumber),
      fEmpPhoneWork: yup
        .string()
        .test('phoneNumberValidation', t('phoneInvalid'), isValidPhoneNumber),
    });
  }, [t]);
  const resolver = useYupValidationResolver(validationSchema);
  const { enqueueSnackbar } = useSnackbar();
  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    reset,
  } = useForm<IFormInput>({
    resolver,
  });

  const handleFormReset = () => {
    reset();
    setReadMode(true);
  };

  const handleFormSubmit = useMemo(() => {
    return handleSubmit((data) => {
      const json: IFormInput = {
        fEmpEmailWork: data.fEmpEmailWork,
        fEmpOfficeFloor: data.fEmpOfficeFloor,
        fEmpOfficeLoc: data.fEmpOfficeLoc,
        fEmpOfficeRoom: data.fEmpOfficeRoom,
        fEmpPhoneMob: data.fEmpPhoneMob,
        fEmpPhoneWork: data.fEmpPhoneWork,
      };
      (async () => {
        try {
          await putEmpOffContactData(json, empBasicData.fEmpId);
          enqueueSnackbar(t('change_saved'), {
            variant: 'success',
          });
          dispatch(fetchEmployee({ empId: empBasicData.fEmpId }));
          setReadMode(true);
        } catch (error) {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        }
      })();
    });
  }, [dispatch, empBasicData.fEmpId, enqueueSnackbar, handleSubmit, t]);

  const onSubmit = () => {
    handleFormSubmit();
  };

  return {
    control,
    companyOffice,
    readMode,
    setReadMode,
    empData,
    errors,
    register,
    onSubmit,
    handleFormReset,
    isLoading,
    canEdit,
  };
};
